import WFS from "ol/format/WFS";
import GML3 from "ol/format/GML3";
import GeoJSON from "ol/format/GeoJSON";
import { LayerPropertyDef } from "../internal";
import { extentToOLExtent } from "../utils";
import Projection from "ol/proj/Projection";
import { createFetchError } from "../../../infra3d/utils/ajax";
/**
 * @class Layer
 */
export class WFSQGIS {
    /**
     * versions
     * WFS 1.1.0 (supported by OL and qgis-server)
     *
     * @constructor
     */
    constructor(baseurl, project = "", opt_options) {
        this.project = "";
        this.version = "1.1.0";
        this._credentials = "omit";
        this._mode = "cors";
        this._headers = {};
        this._writeTransactionXML = function (name, srsName, inserts, updates, deletions) {
            const geoJSONFormat = new GeoJSON();
            return this._wfsformat.writeTransaction(geoJSONFormat.readFeatures(inserts), geoJSONFormat.readFeatures(updates), geoJSONFormat.readFeatures(deletions), {
                featureNS: "http://www.qgis.org/gml",
                featureType: name,
                srsName: srsName,
                version: this.version,
                featurePrefix: "",
                nativeElements: [],
                hasZ: false,
            });
        };
        const options = opt_options || {};
        this.baseurl = new URL(baseurl);
        this.project = project;
        const _bcredentials = typeof options.withCredentials === "undefined" ? true : options.withCredentials;
        this._credentials = _bcredentials ? "include" : "omit";
        this._mode = options.mode || "cors";
        this._headers = options.headers || {};
        this._wfsformat = new WFS({ gmlFormat: new GML3() });
        this.gmlformat = options.gmlformat || "gml3";
        this.geojsonformat = options.geojsonformat || "geojson";
    }
    /**
     * Feature request with options
     *
     * @param options
     * @param onSuccess
     * @param onError
     */
    getFeatures(extent, srid, name, filter, onSuccess, onError) {
        const typeName = this.version < "2.0.0" ? "typeName" : "typeNames";
        const projection = `EPSG:${srid}`;
        const olextent = extentToOLExtent(extent);
        const url = new URL(this.baseurl.toString());
        url.searchParams.set("service", "WFS");
        url.searchParams.set("version", this.version);
        url.searchParams.set("request", "GetFeature");
        url.searchParams.set(typeName, name);
        url.searchParams.set("outputFormat", this.gmlformat);
        url.searchParams.set("srsName", projection);
        if (filter)
            url.searchParams.set("EXP_FILTER", filter);
        url.searchParams.set("bbox", olextent.join(","));
        if (this.project)
            url.searchParams.set("MAP", this.project);
        fetch(url.toString(), {
            credentials: this._credentials,
            mode: this._mode,
            headers: this._headers,
        })
            .then((response) => response
            .text()
            .then((str) => {
            return new window.DOMParser().parseFromString(str, "text/xml");
        })
            .then((gml_document) => {
            const error_node = gml_document.getElementsByTagName("parsererror");
            if (error_node.length > 0)
                throw new Error("Error while parsing the response!");
            const features = this._wfsformat.readFeatures(gml_document, {
                extent: olextent,
                featureProjection: projection,
            });
            const geojson_features = new GeoJSON().writeFeaturesObject(features, {
                dataProjection: new Projection({ code: projection }),
                featureProjection: new Projection({ code: projection }),
            });
            onSuccess(geojson_features);
        }))
            .catch(onError);
    }
    readLayerPropertyDefs(name, onSuccess, onError) {
        const typeName = this.version < "2.0.0" ? "typeName" : "typeNames";
        const url = new URL(this.baseurl.toString());
        url.searchParams.set("service", "WFS");
        url.searchParams.set("version", this.version);
        url.searchParams.set("request", "DescribeFeatureType");
        url.searchParams.set(typeName, name);
        url.searchParams.set("outputFormat", this.gmlformat);
        if (this.project)
            url.searchParams.set("MAP", this.project);
        fetch(url.toString(), {
            credentials: this._credentials,
            mode: this._mode,
            headers: this._headers,
        })
            .then((response) => response
            .text()
            .then((str) => new window.DOMParser().parseFromString(str, "text/xml"))
            .then((dft_document) => {
            const error_node = dft_document.getElementsByTagName("parsererror");
            if (error_node.length > 0)
                throw new Error("Error while parsing the response!");
            onSuccess(LayerPropertyDef.propertydefFromLDescribeFeaturetypeNode(dft_document));
        }))
            .catch(onError);
    }
    readFormatsFromCapabilities(onSuccess, onError) {
        const url = new URL(this.baseurl.toString());
        url.searchParams.set("service", "WFS");
        url.searchParams.set("version", this.version);
        url.searchParams.set("request", "GetCapabilities");
        if (this.project)
            url.searchParams.set("MAP", this.project);
        fetch(url.toString(), {
            credentials: this._credentials,
            mode: this._mode,
            headers: this._headers,
        })
            .then((response) => response
            .text()
            .then((str) => new window.DOMParser().parseFromString(str, "text/xml"))
            .then((cpb_document) => {
            const error_node = cpb_document.getElementsByTagName("parsererror");
            if (error_node.length > 0)
                throw new Error("Error while parsing the response!");
            const supported_formats = [];
            const get_feature = cpb_document.getElementsByTagName("GetFeature");
            if (get_feature.length > 0) {
                const result_format = get_feature[0].getElementsByTagName("ResultFormat");
                if (result_format.length > 0) {
                    for (let i = 0; i < result_format[0].childElementCount; i++) {
                        supported_formats.push(result_format[0].children[i].nodeName);
                    }
                }
            }
            let gmlformat = "gml3";
            let geojsonformat = "geojson";
            if (supported_formats.length > 0) {
                if (supported_formats.indexOf("gml3") > -1) {
                    gmlformat = "gml3";
                }
                else if (supported_formats.indexOf("GML3") > -1) {
                    gmlformat = "GML3";
                }
                if (supported_formats.indexOf("geojson") > -1) {
                    geojsonformat = "geojson";
                }
                else if (supported_formats.indexOf("GeoJSON") > -1) {
                    geojsonformat = "GeoJSON";
                }
            }
            onSuccess(gmlformat, geojsonformat);
        }))
            .catch(onError);
    }
    readLayerOptionsFromCapabilities(onSuccess, onError) {
        const url = new URL(this.baseurl.toString());
        url.searchParams.set("service", "WFS");
        url.searchParams.set("version", this.version);
        url.searchParams.set("request", "GetCapabilities");
        if (this.project)
            url.searchParams.set("MAP", this.project);
        fetch(url.toString(), {
            credentials: this._credentials,
            mode: this._mode,
            headers: this._headers,
        })
            .then((response) => {
            if (!response.ok) {
                throw new Error("Not 2xx response");
            }
            else {
                return response
                    .text()
                    .then((str) => new window.DOMParser().parseFromString(str, "text/xml"))
                    .then((cpb_document) => {
                    const error_node = cpb_document.getElementsByTagName("parsererror");
                    if (error_node.length > 0)
                        throw new Error("Error while parsing the response!");
                    const layers = [];
                    const featureTypes = cpb_document.getElementsByTagName("FeatureType");
                    if (featureTypes) {
                        for (let i = 0; i < featureTypes.length; i++) {
                            const featureType = featureTypes[i];
                            const elements_Name = featureType.getElementsByTagName("Name");
                            const elements_Title = featureType.getElementsByTagName("Title");
                            let layer_editable = false;
                            const operations_elements = featureType.getElementsByTagName("Operations");
                            if (operations_elements.length > 0) {
                                const operations_list = operations_elements[0].getElementsByTagName("Operation");
                                const operations = [];
                                for (let j = 0; j < operations_list.length; j++) {
                                    operations.push(operations_list[j].textContent);
                                }
                                if (operations.indexOf("Insert") > -1 ||
                                    operations.indexOf("Update") > -1 ||
                                    operations.indexOf("Delete") > -1) {
                                    layer_editable = true;
                                }
                            }
                            layers.push({
                                layername: elements_Name.length > 0 ? elements_Name[0].textContent : "",
                                title: elements_Title.length > 0 ? elements_Title[0].textContent : "",
                                editable: layer_editable,
                            });
                        }
                    }
                    onSuccess(layers);
                });
            }
        })
            .catch(onError);
    }
    readGeometryTypeFromFirstFeature(name, onSuccess, onError) {
        const typeName = this.version < "2.0.0" ? "typeName" : "typeNames";
        const url = new URL(this.baseurl.toString());
        url.searchParams.set("service", "WFS");
        url.searchParams.set("version", this.version);
        url.searchParams.set("request", "GetFeature");
        url.searchParams.set(typeName, name);
        url.searchParams.set("outputFormat", this.geojsonformat);
        url.searchParams.set("MAXFEATURES", "1");
        if (this.project)
            url.searchParams.set("MAP", this.project);
        fetch(url.toString(), {
            credentials: this._credentials,
            mode: this._mode,
            headers: this._headers,
        })
            .then((response) => response.json())
            .then((json) => {
            if (json.features.length > 0) {
                if (json.features[0].geometry) {
                    const geomtype = json.features[0].geometry.type;
                    onSuccess(geomtype);
                    return;
                }
            }
            onError(new Error("could not parse geometrytype from first feature"));
        })
            .catch((error) => {
            onError(error);
        });
    }
    pageFeatures(name, start, limit, srid, filter, sort, countonly, onSuccess, onError) {
        const typeName = this.version < "2.0.0" ? "typeName" : "typeNames";
        const projection = `EPSG:${srid}`;
        const url = new URL(this.baseurl.toString());
        url.searchParams.set("service", "WFS");
        url.searchParams.set("version", this.version);
        url.searchParams.set("request", "GetFeature");
        url.searchParams.set(typeName, name);
        url.searchParams.set("outputFormat", this.gmlformat);
        url.searchParams.set("srsName", projection);
        if (!countonly) {
            url.searchParams.set("STARTINDEX", start.toString());
            url.searchParams.set("MAXFEATURES", limit.toString());
        }
        if (filter)
            url.searchParams.set("EXP_FILTER", filter);
        url.searchParams.set("SORTBY", sort);
        url.searchParams.set("RESULTTYPE", countonly ? "hits" : "results");
        if (this.project)
            url.searchParams.set("MAP", this.project);
        if (countonly) {
            fetch(url.toString(), {
                credentials: this._credentials,
                mode: this._mode,
                headers: this._headers,
            }).then((response) => response
                .text()
                .then((str) => new window.DOMParser().parseFromString(str, "text/xml"))
                .then((document) => {
                const error_node = document.getElementsByTagName("parsererror");
                if (error_node.length > 0)
                    throw new Error("Error while parsing the response!");
                onSuccess({ type: "FeatureCollection", features: [] }, parseInt(document.getRootNode().firstChild.getAttribute("numberOfFeatures")));
            })
                .catch(onError));
            return;
        }
        fetch(url.toString(), {
            credentials: this._credentials,
            mode: this._mode,
            headers: this._headers,
        })
            .then((response) => response
            .text()
            .then((str) => new window.DOMParser().parseFromString(str, "text/xml"))
            .then((gml_document) => {
            const error_node = gml_document.getElementsByTagName("parsererror");
            if (error_node.length > 0)
                throw new Error("Error while parsing the response!");
            const features = this._wfsformat.readFeatures(gml_document, {
                extent: [NaN, NaN, NaN, NaN],
                featureProjection: projection,
            });
            const geojson_features = new GeoJSON().writeFeaturesObject(features, {
                dataProjection: new Projection({ code: projection }),
                featureProjection: new Projection({ code: projection }),
            });
            onSuccess(geojson_features, 10);
        }))
            .catch(onError);
    }
    /**
     * Sends transaction to qgis-server
     *
     * @param options
     * @param inserts
     * @param updates
     * @param deletions
     * @param onSuccess
     * @param onError
     */
    applyEdits(name, srid, inserts, updates, deletions, onSuccess, onError) {
        const transaction_xml = this._writeTransactionXML(name, srid, inserts, updates, deletions);
        const serialized = new XMLSerializer().serializeToString(transaction_xml);
        const url = new URL(this.baseurl.toString());
        url.searchParams.set("service", "WFS");
        url.searchParams.set("version", this.version);
        url.searchParams.set("request", "Transaction");
        if (this.project)
            url.searchParams.set("MAP", this.project);
        fetch(url.toString(), {
            method: "POST",
            body: serialized,
            credentials: this._credentials,
            mode: this._mode,
            headers: this._headers,
        })
            .then((res) => res.text())
            .then((str) => new window.DOMParser().parseFromString(str, "text/xml"))
            .then((response_document) => {
            const error_node = response_document.getElementsByTagName("parsererror");
            if (error_node.length > 0)
                throw new Error("Error while parsing the response!");
            const response = this._wfsformat.readTransactionResponse(response_document);
            if (!Object.hasOwnProperty.call(response, "transactionSummary")) {
                const exceptions = response_document.getElementsByTagName("ServiceExceptionReport");
                if (exceptions.length > 0) {
                    const err = createFetchError(400, exceptions[0].innerHTML);
                    throw err;
                }
            }
            const resp_inserts = JSON.parse(JSON.stringify(inserts));
            const resp_updates = JSON.parse(JSON.stringify(updates));
            const resp_deletions = JSON.parse(JSON.stringify(deletions));
            if (Object.hasOwnProperty.call(response, "insertIds")) {
                if (response.insertIds.length == inserts.features.length) {
                    for (let i = 0; i < resp_inserts.features.length; i++) {
                        resp_inserts.features[i].id = response.insertIds[i];
                    }
                }
                else {
                    if (onError) {
                        onError(new Error(`Not all features could be added`));
                    }
                    return;
                }
            }
            if (Object.hasOwnProperty.call(response.transactionSummary, "totalUpdated")) {
                if (response.transactionSummary.totalUpdated != updates.features.length) {
                    if (onError) {
                        onError(new Error("Not all features could be updated"));
                    }
                    return;
                }
            }
            if (Object.hasOwnProperty.call(response.transactionSummary, "totalDeleted")) {
                if (response.transactionSummary.totalDeleted != deletions.features.length) {
                    if (onError) {
                        onError(new Error("Not all features could be deleted"));
                    }
                    return;
                }
            }
            onSuccess(resp_inserts, resp_updates, resp_deletions);
        })
            .catch((error) => {
            return onError(error);
        });
    }
}
//# sourceMappingURL=wfsqgis.js.map